import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

import { RenderingContext, RenderingContextProvider } from './../../../api';
import { createSingleSubject, DEFAULT_FETCH_LEVELS, opLevels, opShareLast } from './../../../utils';
import { WchService } from './../../services/wch.service';
import { defineProperties } from './../../utils/js.utils';
import { distinctSubject } from './../../utils/rx.utils';
import { AbstractBaseComponent } from './../rendering/abstract-base.component';
import { createSetter } from './../rendering/abstract.lifecycle.component';

/* Copyright IBM Corp. 2017 */

const LOGGER = 'ContentPathComponent';

/**
 * Combines the active pieces of the state into one single object
 */
export declare type ComponentState = [RenderingContext, string];

/*
 * The content path component allows to access a component by path
 */
@Component({
  selector: 'wch-contentpath',
  templateUrl: './content.path.component.html',
  styleUrls: ['./content.path.component.css'],
  preserveWhitespaces: false
})
export class ContentPathComponent extends AbstractBaseComponent
  implements OnDestroy, RenderingContextProvider {
  /*
   * Fires whenever the layout mode changed, includes the initial default mode.
   */
  @Output() readonly onLayoutMode: Observable<string>;

  /*
   * The rendering context for the page. This is the context
   * of the element that is referenced by the currently active route.
   */
  @Output() readonly onRenderingContext: Observable<RenderingContext>;

  /*
   * Event that propagates the newly created component
   */
  @Output() readonly onComponent = new EventEmitter<any>();

  /*
   * Accepts the number of levels to be queried
   */
  @Input() levels: string | number | null | undefined;

  /*
  * Set the path to render the page
  */
  @Input() path: string;

  /*
   * Fires whenever the path mode changed.
   */
  @Output() readonly onPath: Observable<string>;

  /**
   * The component state
   */
  readonly onState: Observable<ComponentState>;

  constructor(aWchService: WchService) {
    // default
    super();

    // quick pointer
    const that = this;

    // subjects
    const pathSubject = createSingleSubject<string>();
    const levelsSubject = new BehaviorSubject<
      string | number | null | undefined
    >(DEFAULT_FETCH_LEVELS);

    // init the observable for the mode
    const onPath = (this.onPath = distinctSubject(pathSubject));
    const onLevels = levelsSubject.pipe(
      opLevels,
      distinctUntilChanged()
    );

    // attach to the path
    const onRenderingContext = (this.onRenderingContext = combineLatest(
      onPath,
      onLevels
    ).pipe(
      // decode the path
      switchMap(([path, levels]) =>
        aWchService.getRenderingContextByPath(path, levels)
      ),
      // cache the last value
      opShareLast,
      // end at the right point in time
      takeUntil(that.onOnDestroy)
    ));

    const onLayoutMode = that.onLayoutMode;

    /**
     * Combines the latest changes
     */
    const onComponentState = (this.onState = combineLatest(
      onRenderingContext,
      onLayoutMode
    ));

    // declare the setters
    defineProperties(that, {
      levels: createSetter(levelsSubject, that),
      path: createSetter(pathSubject, that)
    });
  }

  // just dispatch, otherwise the method will not be recognized
  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
